import PlayOnline from './Providers/PlayOnline';
import { SOURCE } from '../platform';

// Central place to gather marketing / attribution fields used in register & deposit flows.
export interface MarketingFields {
    gclid?: string;
    gbraid?: string;
    wbraid?: string;
    fbp?: string;
    fbc?: string;
    source: string;        // web | app
    platformType: string | number;
    fbclid?: string;
    userAgent?: string;
}

interface CollectOpts { }

function resolvePlatformType(): string | number {
    const cfg = (window as any)?.config || {};
    if (cfg.cordova) {
        return cfg.native_platform === 'ios' ? 3 : 4; // historical mapping already in sagas
    }
    // Fallback to backend provided platformType or default (5 used previously for safety)
    return cfg.platformType ?? 5;
}

function resolveSource(): string {
    return (window as any)?.config?.appEnv ? SOURCE.app : SOURCE.web;
}

export function collectMarketingFields(_opts: CollectOpts = {}): MarketingFields {
    const po = PlayOnline;
    const gclid = po.getGclid();
    const gbraid = po.getSavedField('gbraid');
    const wbraid = po.getSavedField('wbraid');
    const fbp = po.getSavedField('_fbp');
    const fbc = po.getSavedField('_fbc');
    const source = resolveSource();
    const platformType = resolvePlatformType();
    const fbclid = po.getSavedField('fbclid');
    const userAgent = typeof window !== 'undefined' && window.navigator ? window.navigator.userAgent : '';

    const out: MarketingFields = {
        source,
        platformType,
    } as MarketingFields;

    /**
     * Assign only when value is a non-empty string / defined primitive.
     * (Empty string, undefined, null are ignored to keep payload lean.)
     *
     * */
    const assign = (k: keyof MarketingFields, v: unknown) => {
        if (v === undefined || v === null || v === false) return;
        if (typeof v === 'string' && v === '') return;
        (out as unknown as Record<string, unknown>)[k as string] = v;
    };
    assign('gclid', gclid);
    assign('gbraid', gbraid);
    assign('wbraid', wbraid);
    assign('fbp', fbp);
    assign('fbc', fbc);
    assign('fbclid', fbclid);
    assign('userAgent', userAgent);
    return out;
}

/**
 * Mutates the provided payload object with collected marketing fields.
 * Will NOT overwrite existing keys already present on payload.
 * @param payload
 * @param opts
 */
export function enrichWithMarketing<T extends Record<string, any>>(payload: T, opts: CollectOpts = {}): T {
    const fields = collectMarketingFields(opts);
    Object.entries(fields).forEach(([k, v]) => {
        if (payload[k] === undefined) (payload as any)[k] = v;
    });
    return payload;
}
